import { notify, TabPanel } from '@/components';
import { useTranslation } from 'react-i18next';
import {
  useUpdateFloorplanGeoItemsMutation,
  FloorplanGeoItemArgs,
} from '@/redux';
import { Button, Space } from 'antd';
import { useCallback, useState } from 'react';
import { ItemListTabPanelGridTreeView } from '@/views';

interface Props {
  communityId: string;
  floorplanId: string;
}

const useSubmit = () => {
  const [update] = useUpdateFloorplanGeoItemsMutation();

  return async (floorplanId: string, values: FloorplanGeoItemArgs[]) => {
    await update({ floorplanId, values })
      .unwrap()
      .then(() => {
        notify.success.t('floorplans.details.saved');
      });
  };
};

export function FloorplanGeoItemsList({ communityId, floorplanId }: Props) {
  const { t } = useTranslation();
  const [editMode, setEditMode] = useState(false);
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  const submit = useSubmit();

  const handleSave = useCallback(async () => {
    const updates = Object.entries(selected).map(([itemId, checked]) => ({
      itemId,
      checked,
    }));

    await submit(floorplanId, updates);
    setEditMode(false);
  }, [selected, floorplanId, submit]);

  const handleCancel = useCallback(() => {
    setEditMode(false);
  }, []);

  return (
    <TabPanel min="auto">
      <TabPanel.Title>{t('items.title')}</TabPanel.Title>
      <TabPanel.Actions>
        <div className="flex justify-between mb-2">
          <div>
            {!editMode ? (
              <Button type="primary" onClick={() => setEditMode(true)}>
                {t('edit')}
              </Button>
            ) : (
              <Space>
                <Button type="primary" onClick={handleSave}>
                  {t('save')}
                </Button>
                <Button onClick={handleCancel}>{t('cancel')}</Button>
              </Space>
            )}
          </div>
        </div>
      </TabPanel.Actions>
      <TabPanel.Body>
        <ItemListTabPanelGridTreeView
          communityId={communityId}
          floorplanId={floorplanId}
          isGeo
          editable={editMode}
          selectedRowKeys={Object.keys(selected).filter((id) => selected[id])}
          onSelectionChange={setSelected}
        />
      </TabPanel.Body>
    </TabPanel>
  );
}
